import { MOWER_IDS } from '../content/mowers';
import type { RaceSize } from '../gen/modes';
import { equippedPaint } from '../meta/economy';
import type { MatchTurn } from '../meta/online/duel';
import {
  isMatchMode,
  MODE_NEEDS,
  RoomCreatedSchema,
  RoomTicketSchema,
  type ClientMessage,
  type RoomMode,
  type ServerMessage,
} from '../meta/online/live';
import type { PostedRun } from '../meta/online/run';
import { LiveSocket, type TicketAnswer } from '../platform/live';
import { onAppShown } from '../platform/native';
import { apiRequest, onConnectionBack } from '../platform/online';
import { ensurePlayer, onlineOn } from './online';
import { saves } from './records';
import { nextView, type FriendTrack, type RaceView } from './raceView';

export {
  safeTick,
  type FriendTrack,
  type MatchResult,
  type RacePlayer,
  type RaceResult,
  type RaceView,
} from './raceView';

/**
 * A live race in the game (docs/LIVE.md): the room as the screens see it, and the way to talk to
 * it. One race at a time ({@link currentRace}). The friend's turns and progress are kept apart
 * from the view (they come many times a second, for the ghost), in {@link RaceSession.friend}.
 */

const freshFriend = (): FriendTrack => ({ inputs: [], marks: [], pct: 0, hashes: [] });

/** Hashes kept for the ghost to check: a few seconds' worth. */
const KEEP_HASHES = 24;

/** What the room's refusals mean to a player. */
function ticketProblem(status: number): string {
  if (status === 404) return 'This race is over.';
  if (status === 409) return 'This race is full.';
  if (status === 403) return "You can't join this race.";
  if (status === 426) return 'Update the game to race.';
  return "Can't join this race.";
}

export class RaceSession {
  view: RaceView;
  friend: FriendTrack = freshFriend();
  private readonly socket: LiveSocket;
  private readonly listeners = new Set<(view: RaceView) => void>();
  private readonly friendListeners = new Set<() => void>();
  /**
   * What this player asked for and the room has not shown yet: sent again on every room update
   * until it shows (a message sent while the line was still opening, or lost to a reconnect).
   */
  private wants: { ready: boolean; rematch: boolean } = { ready: false, rematch: false };
  /** This player's turns this race, and their finished run: sent again after a reconnect. */
  private mine: { inputs: [number, number][]; finish: PostedRun | null } = {
    inputs: [],
    finish: null,
  };
  private wasOpen = false;
  /**
   * Head to head (docs/HEAD_TO_HEAD.md): each seat's turns as the room stamped them (final), by
   * number; and a count that goes up whenever one arrives, so the match screen knows to look.
   */
  private confirmed: MatchTurn[][] = [[], [], [], []];
  turnsSeen = 0;
  /** Head to head: a stamped turn arrived for a tick at or before this one (a rebuild is due). */
  lateFrom = Infinity;

  constructor(
    readonly code: string,
    token: string,
    makeSocket?: ConstructorParameters<typeof LiveSocket>[0]['makeSocket'],
  ) {
    this.view = {
      code,
      line: 'connecting',
      closedWhy: null,
      you: null,
      size: null,
      players: [],
      phase: null,
      start: null,
      result: null,
      mode: null,
      rules: null,
      seats: 2,
      match: null,
      friendLeft: false,
      problem: null,
    };
    this.socket = new LiveSocket({
      code,
      ticket: () => this.ticket(token),
      onMessage: (m) => this.receive(m),
      onLine: (line, why) => {
        if (line === 'open' && this.wasOpen) this.resend();
        if (line === 'open') this.wasOpen = true;
        this.set({ ...this.view, line, closedWhy: why ?? null });
      },
      ...(makeSocket ? { makeSocket } : {}),
    });
  }

  /** Calls `fn` with the view on every change; returns the way to stop. */
  subscribe(fn: (view: RaceView) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Head to head: every seat's turns as far as this phone knows: the room's stamped ones, and this
   * phone's own not yet back from the room (at the tick it played them).
   */
  matchTurns(): MatchTurn[][] {
    const you = this.view.you ?? 0;
    return this.confirmed.map((list, seat) => {
      if (seat !== you) return list;
      const pending = this.mine.inputs.slice(list.length).map(([t, c]): MatchTurn => [t, c]);
      return [...list, ...pending];
    });
  }

  /** Calls `fn` whenever the friend's track moves on; returns the way to stop. */
  onFriend(fn: () => void): () => void {
    this.friendListeners.add(fn);
    return () => this.friendListeners.delete(fn);
  }

  /** The server's clock now. */
  serverNow(): number {
    return this.socket.serverNow();
  }

  ready(): void {
    this.wants.ready = true;
    this.send({ type: 'ready', paints: myPaints() });
  }

  start(): void {
    this.send({ type: 'start' });
  }

  rematch(): void {
    this.wants.rematch = true;
    this.send({ type: 'rematch' });
  }

  sendInput(t: number, c: number): void {
    const n = this.mine.inputs.length;
    this.mine.inputs.push([t, c]);
    this.send({ type: 'input', t, c, n });
  }

  sendProgress(t: number, pct: number, hash: string): void {
    this.send({ type: 'progress', t, pct, hash, n: this.mine.inputs.length });
  }

  finish(run: PostedRun): void {
    this.mine.finish = run;
    this.send({ type: 'finish', run });
  }

  /**
   * Back on the line mid-race: every turn again (the friend keeps each once) and the finish, if
   * any. Anything sent while the line was down, or lost in it, gets there now.
   */
  private resend(): void {
    if (!this.view.start || this.view.result) return;
    this.mine.inputs.forEach(([t, c], n) => this.send({ type: 'input', t, c, n }));
    if (this.mine.finish) this.send({ type: 'finish', run: this.mine.finish });
  }

  /** For tests (dev builds): drops the line as a bad connection would. */
  dropLine(): void {
    this.socket.drop();
  }

  /** Leaves for good (a forfeit mid-race) and lets go of the room. */
  leave(): void {
    this.send({ type: 'leave' });
    this.socket.close();
    if (current === this) current = null;
  }

  /** The device is back (the app shown, the connection returned): reconnect now if down. */
  wake(): void {
    this.socket.retryNow();
  }

  private send(message: ClientMessage): boolean {
    return this.socket.send(message);
  }

  private async ticket(token: string): Promise<TicketAnswer> {
    const answer = await apiRequest(`/rooms/${this.code}/ticket`, { method: 'POST', token });
    if (answer.status === 'network' || answer.status >= 500 || answer.status === 429) {
      return { retry: true };
    }
    const parsed = RoomTicketSchema.safeParse(answer.body);
    if (answer.status === 200 && parsed.success) return { ticket: parsed.data.ticket };
    return { stop: ticketProblem(answer.status) };
  }

  private receive(message: ServerMessage): void {
    // A new race (not the same start sent again after a reconnect): fresh tracks.
    if (message.type === 'countdown' && message.seed !== this.view.start?.seed) {
      this.friend = freshFriend();
      this.wants = { ready: false, rematch: false };
      this.mine = { inputs: [], finish: null };
      this.confirmed = [[], [], [], []];
      this.lateFrom = Infinity;
    }
    // Head to head: every turn comes from the room, stamped with its final tick (both seats').
    if (message.type === 'input' && isMatchMode(this.view.mode)) {
      const list = this.confirmed[message.seat];
      if (list && message.n === list.length) {
        list.push([message.t, message.c]);
        const mine = message.seat === this.view.you ? this.mine.inputs[message.n] : undefined;
        // The friend's turns always change what this phone guessed; its own only if re-stamped.
        if (!mine || mine[0] !== message.t) {
          this.lateFrom = Math.min(this.lateFrom, mine ? Math.min(mine[0], message.t) : message.t);
        }
        // This phone's own turn takes the room's tick from now on.
        if (mine) mine[0] = message.t;
        this.turnsSeen++;
      }
      return;
    }
    if (message.type === 'room') {
      const me = message.players.find((p) => p.seat === message.you);
      if (me && !me.ready) {
        if (this.wants.ready && message.phase === 'waiting') {
          this.send({ type: 'ready', paints: myPaints() });
        }
        if (this.wants.rematch && message.phase === 'done') this.send({ type: 'rematch' });
      }
    }
    if (message.type === 'input' && message.seat !== this.view.you) {
      this.friend.inputs[message.n] = [message.t, message.c];
      for (const fn of this.friendListeners) fn();
      return;
    }
    if (message.type === 'progress' && message.seat !== this.view.you) {
      this.friend.marks.push([message.t, message.n]);
      if (this.friend.marks.length > KEEP_HASHES) this.friend.marks.shift();
      this.friend.pct = message.pct;
      this.friend.hashes.push([message.t, message.hash]);
      if (this.friend.hashes.length > KEEP_HASHES) this.friend.hashes.shift();
      for (const fn of this.friendListeners) fn();
      return;
    }
    this.set(nextView(this.view, message, Date.now()));
  }

  private set(view: RaceView): void {
    this.view = view;
    for (const fn of this.listeners) fn(view);
  }
}

let current: RaceSession | null = null;

/** The race in progress, if any. */
export function currentRace(): RaceSession | null {
  return current;
}

let woken = false;
function wakeOnReturn(): void {
  if (woken) return;
  woken = true;
  onAppShown(() => current?.wake());
  onConnectionBack(() => current?.wake());
}

/** Joins a race room by its code (from an invite link): the session, or what went wrong. */
export async function joinRace(code: string): Promise<RaceSession | string> {
  if (!onlineOn()) return 'Turn on online play in Settings to race.';
  const player = await ensurePlayer();
  if (!player) return "Can't reach the server right now.";
  current?.leave();
  wakeOnReturn();
  current = new RaceSession(code, player.token);
  // For the race e2e (dev builds): the session, to drop its line.
  if (import.meta.env.DEV) (window as unknown as { __race?: RaceSession }).__race = current;
  return current;
}

/**
 * This player's paint on each mower a match or race can lend (the friend sees it: head to head's
 * mowers, and a race's ghost, which may be any mower).
 */
function myPaints(): Record<string, string> {
  const save = saves().save;
  const out: Record<string, string> = {};
  for (const id of MOWER_IDS) {
    const paint = equippedPaint(save, id);
    if (paint) out[id] = paint;
  }
  return out;
}

/** Opens a race room of this size and joins it as the inviter: the session and its link. */
export async function createRace(
  size: RaceSize,
  mode: RoomMode = 'race',
  /** A party of up to four (docs/PARTY.md), or two. */
  seats: 2 | 4 = 2,
): Promise<{ session: RaceSession; url: string } | string> {
  if (!onlineOn()) return 'Turn on online play in Settings to race.';
  const player = await ensurePlayer();
  if (!player) return "Can't reach the server right now.";
  const answer = await apiRequest('/rooms', {
    method: 'POST',
    token: player.token,
    body: { mode, size, ...(seats > 2 ? { seats } : {}) },
  });
  if (answer.status === 'network') return "Can't reach the server right now.";
  if (answer.status === 426) {
    return seats > 2
      ? 'Update the game to play in a party.'
      : `Update the game to ${MODE_NEEDS[mode]}.`;
  }
  const parsed = RoomCreatedSchema.safeParse(answer.body);
  if (answer.status !== 201 || !parsed.success) return "Can't open a race right now.";
  const session = await joinRace(parsed.data.code);
  return typeof session === 'string' ? session : { session, url: parsed.data.url };
}
